import React, { useEffect, useState, useRef } from 'react';
import style from './LatestActivity.scss.json';
import './LatestActivity.scss';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import { LinkedLiveUpdate } from 'lincd-server-utils/lib/utils/LinkedLiveUpdates';
import { motion, AnimatePresence } from 'framer-motion';
import { InteractiveGlobe } from '../atoms/InteractiveGlobe';
import { cl } from 'lincd/lib/utils/ClassNames';

const MAX_MESSAGES = 100;
const DISPLAY_LIMIT = 7;
const POLLING_INTERVAL = 10000; // 10 seconds
const ROLLING_INTERVAL = 800; // 0.8 seconds p

interface LatestActivityProps {
  live?: boolean;
  event?: string;
}

// generate a unique key for each message based on timestamp and content
// to make sure we don't show the same message multiple times
const generateKey = (msg) => {
  return `${msg.timestamp}_${encodeURIComponent(msg.data?.message || '')}`;
};

const LatestActivity = React.memo(({ live = false, event }: LatestActivityProps) => {
  const [updateMessages, setUpdateMessages] = useState([]);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [unseenQueue, setUnseenQueue] = useState([]); // queue for new messages that haven't been shown yet

  const lastUpdatedTimeRef = useRef(Date.now() - 48 * 60 * 60 * 1000); // get updates from the last 48 hours
  const shownTimestampsRef = useRef(new Set()); // used to prevent showing the same message multiple times

  // fetch live updates
  useEffect(() => {
    const fetchUpdates = async (initialRequest: boolean) => {
      const newUpdates = await LinkedLiveUpdate.getUpdatesSince(
        lastUpdatedTimeRef.current,
        MAX_MESSAGES, // limit to the last MAX_MESSAGES updates
      );
      if (!newUpdates?.length) return;

      // update the timestamp for next fetch
      lastUpdatedTimeRef.current = Date.now();

      // if more than 25 messages, slice to the last 25
      // for example 40 messages - 25 = splice(15) --> take from 15th position until 40th
      if (initialRequest && newUpdates.length > MAX_MESSAGES) {
        newUpdates.splice(0, newUpdates.length - MAX_MESSAGES);
      }

      // append new unique messages to the updateMessages state
      setUpdateMessages((prev) => {
        const existingKeys = new Set(prev.map((m) => generateKey(m)));
        const uniqueNew = newUpdates.filter(
          (m) => !existingKeys.has(generateKey(m)),
        );
        const combined = [...prev, ...uniqueNew].slice(-MAX_MESSAGES);
        return combined;
      });

      // add only unseen messages to the unseenQueue for rolling display
      setUnseenQueue((prev) => {
        const newQueue = newUpdates.filter((msg) => {
          const key = generateKey(msg);
          return (
            !shownTimestampsRef.current.has(key) &&
            !prev.some((m) => generateKey(m) === key) &&
            // ensure don't add messages that are already visible
            !visibleMessages.some((m) => generateKey(m) === key)
          );
        });
        return [...prev, ...newQueue];
      });
    };

    const interval = setInterval(fetchUpdates, POLLING_INTERVAL);
    fetchUpdates(true); // initial fetch
    return () => clearInterval(interval);
  }, []);

  // rolling effect: show new unseenQueue messages one by one
  useEffect(() => {
    if (unseenQueue.length === 0) return;

    const interval = setInterval(() => {
      const next = unseenQueue[0];
      if (!next) return;

      // add visible list message while keeping DISPLAY_LIMIT
      const key = generateKey(next);
      setVisibleMessages((prev) => {
        const exists = prev.some((m) => generateKey(m) === key);
        if (exists) return prev; // don't add if already exists

        const updated = [...prev, next];
        if (updated.length > DISPLAY_LIMIT) updated.shift();
        return updated;
      });

      // mark this message as shown and remove it from the queue
      shownTimestampsRef.current.add(key);
      setUnseenQueue((prev) => prev.slice(1));
    }, ROLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [unseenQueue]);

  console.log(updateMessages, 'updateMessages');
  console.log(visibleMessages, 'visibleMessages');

  return (
    <div>
      <div className={cl(style.welcome, live && style.live)}>
        <InteractiveGlobe event={event} />
        <div className={style.flags}>
          {visibleMessages.length > DISPLAY_LIMIT - 2 && (
            <div className={cl(style.fade, style.fade1)}></div>
          )}
          {visibleMessages.length === 0 && updateMessages.length === 0 ? (
            <p className={style.emptyActivity}>No recent activity</p>
          ) : (
            <ul className={style.messageList}>
              <AnimatePresence initial={false}>
                {visibleMessages.map((msg) => (
                  <motion.li
                    key={generateKey(msg)}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -30 }}
                    transition={{ duration: 0.2 }}
                    layout="position"
                  >
                    {msg?.data?.countryCode
                      ? getUnicodeFlagIcon(msg?.data?.countryCode)
                      : null}{' '}
                    {msg?.data?.message}{' '}
                    {msg?.data?.endIcon && (
                      <img
                        src={msg.data.endIcon.toLowerCase()}
                        alt="medal"
                        className={style.medalIcon}
                      />
                    )}
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}
          {visibleMessages.length > DISPLAY_LIMIT - 2 && (
            <div className={cl(style.fade, style.fade2)}></div>
          )}
        </div>
      </div>
    </div>
  );
});

export default LatestActivity;
